// \src\pages\vendedores\page.tsx

import { useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useAdmin } from "@/context/AdminContext";

const storeLabels: Record<string, string> = {
  central: "LOJA CENTRAL",
  filial: "MZ OUTLET",
};

function getLocalDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function isSellerAvailable(seller: {
  available: boolean;
  inactiveUntil: string;
}): boolean {
  if (seller.available) {
    return true;
  }

  if (!seller.inactiveUntil) {
    return false;
  }

  return seller.inactiveUntil <= getLocalDateString();
}

function shuffleItems<T>(items: T[]): T[] {
  const shuffled = [...items];

  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
}

export default function Vendedores() {
  const { data } = useAdmin();
  const promoProducts = data.products.filter(p => data.promotions.includes(p.id));
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => { if (trackRef.current) trackRef.current.style.animationPlayState = "paused"; };
  const handleMouseLeave = () => { if (trackRef.current) trackRef.current.style.animationPlayState = "running"; };

  const doubled = [...promoProducts, ...promoProducts];

  const centralSellers = useMemo(
    () => shuffleItems(data.sellers.filter(s => s.store === "central" && isSellerAvailable(s))),
    [data.sellers],
  );
  const filialSellers = useMemo(
    () => shuffleItems(data.sellers.filter(s => s.store === "filial" && isSellerAvailable(s))),
    [data.sellers],
  );

  return (
    <>
      <Navbar />
      <main className="pt-20 min-h-screen bg-background-50">
        <section className="bg-background-200 border-b border-background-300 py-8 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <nav className="text-xs text-foreground-400 mb-3 font-label">
              <Link to="/" className="hover:text-primary-500 cursor-pointer">Início</Link>
              <span className="mx-2">/</span>
              <span className="text-primary-500">Vendedores</span>
            </nav>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">Vendedores</h1>
            <p className="text-foreground-500 text-sm mt-2">Fale diretamente com nossos especialistas</p>
          </div>
        </section>

        {promoProducts.length > 0 && (
          <section className="w-full py-10 bg-primary-50 overflow-hidden border-b border-primary-100">
            <div className="max-w-7xl mx-auto px-4 mb-6">
              <h2 className="text-lg font-heading font-bold text-foreground-950">Promoções em Destaque</h2>
              <p className="text-foreground-500 text-xs">Passe o mouse para pausar · Clique para ver detalhes</p>
            </div>
            <div className="relative w-full overflow-hidden" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
              <div ref={trackRef} className="flex gap-5 w-max promo-track animate-scroll-left" style={{ animationDuration: "28s", paddingLeft: "16px" }}>
                {doubled.map((product, i) => (
                  <Link key={`${product.id}-${i}`} to={`/produto/${product.slug}`} className="shrink-0 w-48 bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-all cursor-pointer group">
                    <div className="relative h-36">
                      <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      {product.promoLabel && <span className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs font-label font-bold px-2 py-0.5 rounded">{product.promoLabel}</span>}
                    </div>
                    <div className="p-3">
                      <p className="text-foreground-900 font-label font-semibold text-xs leading-tight line-clamp-2">{product.name}</p>
                      {product.code && <span className="inline-flex items-center bg-primary-50 border border-primary-100 text-primary-700 text-[11px] font-label font-semibold px-2 py-0.5 rounded mt-1">Código: {product.code}</span>}
                      {product.showPrice && <p className="text-primary-600 font-heading font-bold text-sm mt-1">R$ {product.price.toFixed(2).replace(".", ",")}</p>}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 space-y-16">
          {[
            { key: "central", sellers: centralSellers, label: "LOJA CENTRAL" },
            { key: "filial", sellers: filialSellers, label: "MZ OUTLET" },
          ].map((group) => (
            <div key={group.key}>
              <div className="text-center mb-10">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg">
                    <i className={group.key === "central" ? "ri-store-2-line" : "ri-store-3-line"} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">{group.label}</h2>
                </div>
                <p className="text-foreground-500 text-sm max-w-md mx-auto">
                  {group.key === "central"
                    ? "Nossa equipe da matriz pronta para te atender com toda dedicação e experiência."
                    : "Profissionais da MZ Outlet preparados para oferecer o mesmo atendimento de qualidade que você já conhece."}
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
                {group.sellers.map((seller) => (
                  <div key={seller.id} className="flex flex-col items-center text-center group">
                    <div className="relative mb-3">
                      <button onClick={() => { setSelectedPhoto(seller.photo); setSelectedName(seller.name); }} className="cursor-pointer focus:outline-none" aria-label={`Ver foto de ${seller.name}`}>
                        <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-3 border-background-200 group-hover:border-primary-400 transition-all duration-300 group-hover:scale-105">
                          <img src={seller.photo} alt={seller.name} className="w-full h-full object-cover object-top" />
                        </div>
                      </button>
                      {seller.available && <span className="absolute bottom-1 right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-background-50" />}
                    </div>
                    <p className="font-label font-semibold text-foreground-900 text-xs md:text-sm leading-tight mb-0.5">{seller.name}</p>
                    <p className="text-foreground-500 text-xs mb-1 leading-tight">{seller.role}</p>
                    {!seller.available && (
                      <span className="text-xs text-foreground-400 italic mb-1">Indisponível</span>
                    )}
                    <a href={`https://wa.me/${seller.whatsapp}`} target="_blank" rel="nofollow noreferrer" className="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-background-50 px-3 py-1.5 rounded-full text-xs font-label font-medium transition-all cursor-pointer whitespace-nowrap">
                      <i className="ri-whatsapp-line text-sm" /> WhatsApp
                    </a>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>

        <section className="bg-background-100 border-t border-background-200 py-12 px-4 text-center">
          <h3 className="text-2xl font-heading font-bold text-foreground-950 mb-3">Precisa de ajuda para escolher?</h3>
          <p className="text-foreground-600 text-sm max-w-md mx-auto mb-6">Nossos especialistas estão prontos para te orientar na escolha dos melhores materiais para sua obra.</p>
          <a href={data.settings.socialWhatsapp} target="_blank" rel="nofollow noreferrer" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-md font-label font-semibold text-sm cursor-pointer whitespace-nowrap transition-all">
            <i className="ri-whatsapp-line" /> Falar com um Especialista
          </a>
        </section>
      </main>

      {selectedPhoto && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => { setSelectedPhoto(null); setSelectedName(null); }}>
          <div className="relative max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <img src={selectedPhoto} alt={selectedName || "Vendedor"} className="w-full rounded-2xl object-cover" />
            {selectedName && <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 rounded-b-2xl"><p className="text-background-50 font-heading font-bold text-lg">{selectedName}</p></div>}
            <button onClick={() => { setSelectedPhoto(null); setSelectedName(null); }} className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-black/50 hover:bg-black/70 text-background-50 rounded-full cursor-pointer transition-colors">
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}